import { Page, Text, View, Document, PDFDownloadLink, StyleSheet, Image } from '@react-pdf/renderer'
import QRCode from 'qrcode'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { SpinnerIcon } from 'components/Icons/SpinnerIcon'
import { IBusinessCustomerUserAccountPDFData } from 'features/BusinessCustomer/types'
import { GTBusinessCustomerUserAccount } from 'gql/types.generated'
import { mapBusinessCustomerUserAccountToQR } from 'features/BusinessCustomer/utils'

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #1b0f0f;
`
const styles = StyleSheet.create({
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    width: 431,
    height: 'auto',
    gap: 15
  },
  data: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  page: {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    marginHorizontal: 'auto',
    paddingVertical: 24
  },
  qrImage: {
    width: 115
  },
  row: {
    flexDirection: 'row',
    display: 'flex',
    gap: 16,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: 24,
    border: '1px solid #EAEAFF',
    color: '#423f9a',
    alignItems: 'center'
  },
  fieldLabel: {
    fontSize: 6,
    fontWeight: 300,
    color: '#423F9A'
  },
  fieldValue: {
    fontSize: 10,
    fontWeight: 800,
    color: '#423F9A'
  },
  dataItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  link: {
    fontSize: 'inherit',
    color: 'inherit',
    textDecoration: 'none',
    fontFamily: 'inherit'
  },
  divider: {
    borderLeft: '1px solid #EAEAFF',
    height: 125,
    width: 2,
    borderStyle: 'dashed'
  }
})

const DataItem = ({ label, value }: { label: string; value: string }): ReactElement => (
  <View style={styles.dataItem}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <Text style={styles.fieldValue}>{value}</Text>
  </View>
)

const Data = ({ data }: { data: GTBusinessCustomerUserAccount }): ReactElement => {
  const { id, name, phone, email } = data
  return (
    <View style={styles.data}>
      <DataItem label="User Account ID" value={id} />
      <DataItem label="Name" value={name} />
      <DataItem label="Phone" value={phone ?? ''} />
      <DataItem label="Email" value={email ?? ''} />
    </View>
  )
}

const QRDocument = ({ data }: { data: IBusinessCustomerUserAccountPDFData[] }): ReactElement => {
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.listContainer}>
          {data.map((userData) => {
            return (
              <View key={userData.id}>
                <View style={styles.row}>
                  <Image style={styles.qrImage} src={userData.qrImageBase64} />
                  <View style={styles.divider} />
                  <Data data={userData} />
                </View>
              </View>
            )
          })}
        </View>
      </Page>
    </Document>
  )
}

const Container = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

export const DownloadPdfLink = ({
  data
}: {
  data: IBusinessCustomerUserAccountPDFData | IBusinessCustomerUserAccountPDFData[]
}): ReactElement => {
  const [pdfData, setPDFData] = useState<IBusinessCustomerUserAccountPDFData[]>([])
  const [generatingQrLoading, setGeneratingQrLoading] = useState(false)

  const generateQr = async (userAccount: IBusinessCustomerUserAccountPDFData): Promise<void> => {
    setGeneratingQrLoading(true)
    let qrImageBase64 = ''
    try {
      qrImageBase64 = await QRCode.toDataURL(mapBusinessCustomerUserAccountToQR(userAccount), {
        margin: 1
      })
    } catch (error) {
      //do nothing
    }
    setPDFData((prev) => [...prev, { ...userAccount, qrImageBase64 }])
    setGeneratingQrLoading(false)
  }

  const generateQrForAll = async (userAccounts: IBusinessCustomerUserAccountPDFData[]): Promise<void> => {
    setGeneratingQrLoading(true)
    const newQrData = []
    for (const account of userAccounts) {
      const qrImageBase64 = await QRCode.toDataURL(mapBusinessCustomerUserAccountToQR(account), {
        margin: 1
      })
      newQrData.push({ ...account, qrImageBase64 })
    }
    setPDFData(newQrData)
    setGeneratingQrLoading(false)
  }

  useEffect(() => {
    if (Array.isArray(data)) {
      generateQrForAll(data)
    } else {
      generateQr(data)
    }
  }, [])

  if (generatingQrLoading)
    return (
      <div>
        <FormattedMessage id="businessCustomer.downloadPdf" defaultMessage="Download QR" />
        <SpinnerIcon />
      </div>
    )

  return (
    <>
      <PDFDownloadLink
        document={<QRDocument data={pdfData} />}
        fileName="business-customer-user-account-data.pdf"
        style={styles.link}
      >
        <Container>
          <FormattedMessage id="businessCustomer.downloadPdf" defaultMessage="Download QR" />
          {generatingQrLoading ? <StyledSpinnerIcon /> : null}
        </Container>
      </PDFDownloadLink>
    </>
  )
}
